import {
  ActionIcon,
  Combobox,
  ComboboxProps,
  Divider,
  Grid,
  Group,
  InputBase,
  ScrollArea,
  useCombobox,
} from '@mantine/core'
import { type GetInputPropsReturnType } from '@mantine/form/lib/types'
import { getCountryFlagFromCountryCode } from '@v2/commons/utils/phone'
import { PhoneNumberValidation } from '@v2/commons/utils/validators/validators.type'
import countries from 'i18n-iso-countries'
import en from 'i18n-iso-countries/langs/en.json'
import parsePhoneNumber, {
  AsYouType,
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  validatePhoneNumberLength,
} from 'libphonenumber-js'
import { useEffect, useRef, useState } from 'react'

import { Icons } from 'src/common/components/ui'

import styles from './phone-number-select.module.scss'

interface PhoneNumberSelect {
  initialCountryCode: CountryCode
  setCountryCode: React.Dispatch<React.SetStateAction<CountryCode>>
  countryCode: CountryCode
  readOnly?: boolean
  label?: string
  withAsterisk?: boolean
}

const PhoneNumberSelect = ({
  initialCountryCode,
  setCountryCode,
  countryCode,
  withAsterisk,
  ...props
}: PhoneNumberSelect & ComboboxProps) => {
  countries.registerLocale(en)
  const [search, setSearch] = useState('')
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      combobox.focusTarget()
      setSearch('')
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput()
    },
  })

  const countriesFullName = countries.getNames('en', { select: 'official' })
  const countriesCodes = getCountries()
  const dropDownOptions = countriesCodes
    .map((currentCode) => ({
      name: countriesFullName[currentCode],
      flag: getCountryFlagFromCountryCode(currentCode),
      code: currentCode,
      callingCode: getCountryCallingCode(currentCode) as CountryCode,
      disabled: currentCode === 'US' ? false : true, // remove in the future
    }))
    .filter(({ name }) => !!name)
    .sort((a, b) => {
      if (a.code === initialCountryCode || a.name < b.name) {
        return -1
      }
      return 1
    })

  const searchCountry = (options: typeof dropDownOptions) => {
    const isSearchByDialingCode = !!Number(search) || search === '+'
    if (isSearchByDialingCode) {
      return options.filter((country) =>
        `+${country.callingCode?.toLowerCase()}`.includes(search.trim()),
      )
    }
    return options.filter(
      (country) =>
        country.name?.toLowerCase()?.includes(search.toLowerCase().trim()) ||
        country.code?.toLowerCase()?.includes(search.toLowerCase().trim()),
    )
  }

  const getCountryOptionByCoude = (code: CountryCode) =>
    dropDownOptions.find((country) => country.code === code)

  const options = searchCountry(dropDownOptions).map((country) => (
    <Combobox.Option
      value={country.code}
      key={country.code}
      disabled={country.disabled}
    >
      <Grid gutter={10} mb={5}>
        <Grid.Col span={4} className={styles['dropdown-root']}>
          {`${country.flag} +${country.callingCode}`}
        </Grid.Col>
        <Divider orientation="vertical" p={5} />

        <Grid.Col span={7} className={styles['dropdown-root']}>
          {country.name}
        </Grid.Col>
      </Grid>
    </Combobox.Option>
  ))

  return (
    <Combobox
      {...props}
      store={combobox}
      withinPortal={false}
      width={300}
      position="bottom-start"
      onOptionSubmit={(val) => {
        setCountryCode(val as CountryCode)
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        <ActionIcon
          variant="transparent"
          disabled={props?.disabled}
          className={styles['action-icon']}
          onClick={() => combobox.toggleDropdown()}
          w={'100%'}
          c="black"
        >
          {countryCode && (
            <Group gap={5}>
              <Group gap={2}>
                <span>{`${getCountryOptionByCoude(countryCode)?.flag}`}</span>
                <Icons.ArrowSelectMenu height={12} width={12} />
              </Group>
              <span>
                {`+${getCountryOptionByCoude(countryCode)?.callingCode}`}
              </span>
              <Divider orientation="vertical" size="sm" />
            </Group>
          )}
        </ActionIcon>
      </Combobox.Target>

      <Combobox.Dropdown id="country-search">
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder="Search dialing code"
          id="country-search-bars"
        />
        <Combobox.Options>
          <ScrollArea.Autosize type="scroll" mah={200}>
            {options.length > 0 ? (
              options
            ) : (
              <Combobox.Empty>Nothing found</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

type InputPhoneNumber = {
  initialCountryCode: CountryCode
  initialValue?: string
  readOnly?: boolean
  label?: string
  withAsterisk?: boolean
} & GetInputPropsReturnType

const PhoneNumberInput = ({
  initialCountryCode = 'US',
  onChange,
  defaultValue,
  initialValue = '',
  readOnly = false,
  withAsterisk = false,
  label = 'Phone Number',
  ...props
}: InputPhoneNumber) => {
  const [phoneValue, setPhoneValue] = useState(initialValue)
  const [countryCode, setCountryCode] =
    useState<CountryCode>(initialCountryCode)
  const previousValue = useRef<string>('')

  const onChangeInputNewItem = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { inputType } = event.nativeEvent as InputEvent
    const { value } = event.currentTarget
    const asYouTypeInstance = new AsYouType(countryCode)

    let currentPhoneValue = value.replace(/\D/g, '')

    if (
      validatePhoneNumberLength(currentPhoneValue, countryCode) ===
        PhoneNumberValidation.LONG &&
      inputType !== 'insertFromPaste'
    ) {
      currentPhoneValue = previousValue.current
    }

    if (!currentPhoneValue || previousValue.current !== currentPhoneValue) {
      asYouTypeInstance.input(currentPhoneValue)
      const barePhoneNumber = asYouTypeInstance.getNumber()
      const nationalFormattedVal = barePhoneNumber?.formatNational() ?? ''
      onChange(barePhoneNumber?.number)
      setPhoneValue(nationalFormattedVal ?? '')
      previousValue.current = nationalFormattedVal ?? ''
    }
  }

  useEffect(() => {
    const phoneNumber = parsePhoneNumber(phoneValue, countryCode)
    if (phoneNumber) {
      setPhoneValue(phoneNumber.formatNational())
      onChange(phoneNumber.number)
    }
  }, [countryCode])

  return (
    <InputBase
      label={label}
      leftSection={
        <PhoneNumberSelect
          initialCountryCode={initialCountryCode}
          setCountryCode={setCountryCode}
          countryCode={countryCode}
          disabled={readOnly}
        />
      }
      leftSectionWidth={'100px'}
      value={phoneValue}
      {...props}
      onChange={onChangeInputNewItem}
      readOnly={readOnly}
      withAsterisk={true}
    />
  )
}
export default PhoneNumberInput
